import { LucideCheckCircle } from "lucide-react";
import React from "react";
import { FiAward, FiBarChart, FiBarChart2, FiEdit3, FiShare2, FiTarget, FiUsers } from "react-icons/fi";
import { LuClipboardList, LuPenTool, LuRefreshCw, LuShare2, LuTrendingUp } from "react-icons/lu";

export const organicGrowthHeroData = {
  backgroundImage: "/images/serviceBanners/ORGANICGROWTH&CONTENTENGINE.webp",
  whiteImage: "/images/serviceBanners/ORGANICGROWTH&CONTENTENGINEWhite.webp",
  subTitle: "Organic Growth & Content Engine Services",
  title: (
    <>
      High Performance <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Organic Growth </span> System
    </>
  ),
  description:
    "We build long term organic growth systems that improve visibility, strengthen audience trust, and drive consistent content led traffic without paid ads through strategic SEO focused content marketing and optimization.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("AI Start clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("AI Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "SEO Driven Content Strategy",
      desc: "Optimized for speed and sub-second inference.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Audience Growth System",
      desc: "Cloud-agnostic deployments (AWS/GCP/Azure).",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Content Distribution Engine",
      desc: "Enterprise-grade model governance and privacy.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Authority Building Framework",
      desc: "Clean, maintainable, and monitorable pipelines.",
      iconColor: "#34d399",
    },
  ],
};

export const organicGrowthAndProcessData = {
  buildSubtitle: "ORGANIC GROWTH & CONTENT ENGINE",
  buildTitle: "Organic Growth & Content Engine Solutions for Every Business Requirement",
  buildCards: [
    {
      title: "Content Strategy Development",
      desc: "Build structured content plans based on keyword research, audience intent, and business goals.",
      icon: FiTarget,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "SEO Content Production",
      desc: "Create optimized blogs, articles, and website content designed to rank and attract organic traffic.",
      icon: FiEdit3,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Content Distribution System",
      desc: "Distribute content across search engines, social platforms, and digital channels for maximum reach.",
      icon: FiShare2,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Authority Building Content",
      desc: "Develop high value content like guides, case studies, and thought leadership articles.",
      icon: FiAward,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
    {
      title: "Audience Growth Optimization",
      desc: "Improve reach, engagement, and retention through content driven strategies.",
      icon: FiUsers,
      colorClass: "text-[#ec4899]",
      bgClass: "bg-[#ec4899]/10",
      borderClass: "border-[#ec4899]/20 hover:border-[#ec4899]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(236,72,153,0.25)]",
    },
    {
      title: "Performance Tracking System",
      desc: "Measure organic traffic, rankings, engagement, and content performance.",
      icon: FiBarChart2,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
  ],
  processSubtitle: "ORGANIC GROWTH & CONTENT ENGINEERING FRAMEWORK",
  processTitle: "How We Build Scalable Organic Growth Systems",
  processSteps: [
    {
      step: "01",
      title: "Content Strategy Planning",
      description: "Define content direction based on keyword research, audience intent, and business growth goals.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Agile Content Execution",
      description: "Publish and refine content in structured cycles to improve reach and engagement over time.",
      icon: LuRefreshCw,
    },
    {
      step: "03",
      title: "Content Development System",
      description: "Create SEO optimized blogs, articles, and digital content for organic traffic growth.",
      icon: LuPenTool,
    },
    {
      step: "04",
      title: "Content Distribution System",
      description: "Distribute content across search engines, social platforms, and digital channels for maximum visibility.",
      icon: LuShare2,
    },
    {
      step: "05",
      title: "Performance Optimization",
      description: "Improve rankings, engagement, and traffic using data driven insights.",
      icon: LuTrendingUp,
    },
    {
      step: "06",
      title: "Quality Assurance System",
      description: "Ensure content consistency, accuracy, and brand alignment across all platforms.",
      icon: LucideCheckCircle,
    },
  ],
  benefitsSubtitle: "WHY INVEST IN ORGANIC GROWTH SYSTEMS?",
  benefitsTitle: "Why Businesses Choose Organic Growth Solutions",
  benefits: [
    "Sustainable long term traffic growth",
    "Strong brand authority building",
    "Cost effective acquisition channel",
    "High quality audience engagement",
    "Consistent visibility across platforms",
    "SEO driven growth system",
    "Scalable content engine model",
  ],
  logoImage: "/images/serviceBanners/OrganicGrowthBanner.webp",
  whiteLogoImage: "/images/serviceBanners/OrganicGrowthW.webp",
};

export const aiTechnologiesData = {
  techTitle: "OUR EXTENSIVE TOOL STACK",
  technologies: [
    { name: "TensorFlow", icon: "RestAPI" },
    { name: "PyTorch", icon: "Code" },
    { name: "OpenAI", icon: "Provider" },
    { name: "Python", icon: "Dart" },
    { name: "Docker", icon: "Vite" },
    { name: "Kubernetes", icon: "Firebase" },
  ],
  trustTitle: "TRUSTED BY INNOVATORS WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle" },
  ],
};

export const organicGrowthAboutData = [
  {
    title: "Content Marketing Services for Sustainable Organic Growth",
    content: (
      <>
        Building organic visibility requires more than publishing content regularly. Professional <b className="text-text-primary font-semibold">Content Marketing Services</b> help
        businesses attract the right audience, establish credibility, and create lasting relationships through valuable information. Many organizations struggle to generate
        consistent traffic because their content lacks structure, direction, and alignment with customer intent. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, content-driven growth strategies are designed around audience behavior, search demand, and business
        objectives to help brands build a strong foundation for long-term digital success.
      </>
    ),
  },
  {
    title: "SEO Content Writing Services That Increase Visibility",
    content: (
      <>
        Search engines reward content that delivers value, relevance, and a positive user experience. Reliable{" "}
        <b className="text-text-primary font-semibold">SEO Content Writing Services</b> help businesses improve discoverability by creating informative content that aligns with
        customer needs and search intent. Companies that invest in strategic content creation often experience stronger engagement because visitors find answers to their questions
        through meaningful and well-researched resources. Tycho Technologies develops content frameworks that improve visibility while supporting brand authority and customer
        trust.
      </>
    ),
  },
  {
    title: "Organic Growth Marketing Services for Audience Expansion",
    content: (
      <>
        Sustainable growth comes from attracting audiences who are genuinely interested in your products, services, or expertise. Effective{" "}
        <b className="text-text-primary font-semibold">Organic Growth Marketing Services</b> help organizations increase reach through consistent content development, audience
        engagement, and strategic visibility improvements. Businesses that focus on organic growth often build stronger customer relationships because trust develops naturally over
        time. A well-planned content ecosystem helps improve retention, strengthen brand awareness, and support long-term business objectives.
      </>
    ),
  },
  {
    title: "Why Businesses Invest in Content Strategy Services",
    content: (
      <>
        The demand for structured <b className="text-text-primary font-semibold">Content Strategy Services</b> continues to grow as businesses seek predictable and scalable growth
        opportunities. Companies working with a trusted <b className="text-text-primary font-semibold">Content Marketing Agency India</b> often focus on creating content ecosystems
        that support visibility, engagement, and customer acquisition. Businesses partnering with <b className="text-text-primary font-semibold">Tycho Technologies</b> gain access
        to customized content strategies designed around measurable goals and market opportunities. A strategic approach to content marketing helps organizations strengthen
        authority, improve search presence, and create sustainable business growth without relying heavily on paid advertising.
      </>
    ),
  },
];

export const organicGrowthFaqs = [
  {
    q: "What is the difference between organic growth marketing and paid advertising?",
    a: "Organic growth marketing focuses on building long-term visibility through SEO, content creation, and audience engagement, while paid advertising generates traffic through sponsored campaigns. Organic strategies help businesses create sustainable traffic and brand authority over time.",
  },
  {
    q: "How does an organic content engine help businesses grow consistently?",
    a: "An organic content engine creates a structured system for producing, distributing, and optimizing content. This helps businesses attract qualified visitors, improve search rankings, and generate ongoing traffic without relying heavily on paid campaigns.",
  },
  {
    q: "What types of content are most effective for organic growth?",
    a: "SEO-focused blogs, industry guides, case studies, thought leadership articles, resource pages, and educational content are highly effective for organic growth because they address user intent and build trust with potential customers.",
  },
  {
    q: "How does Tycho Technologies build a scalable organic growth strategy?",
    a: "Tycho Technologies develops organic growth strategies through keyword research, audience analysis, SEO content planning, content distribution, and performance tracking. This structured approach helps businesses improve visibility and create sustainable long-term growth.",
  },
  {
    q: "Why should businesses choose Tycho Technologies for organic growth and content marketing services?",
    a: "Tycho Technologies focuses on building complete content ecosystems that support organic traffic growth, audience engagement, and brand authority. The goal is to create measurable business outcomes through consistent, SEO-driven content strategies.",
  },
];
